import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router'
import { ChevronLeft, ChevronRight, ExternalLink, Search, Video } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { ErrorState } from '@/components/composites/states'
import { useQueryClient } from '@tanstack/react-query'
import { getErrorMessage } from '@/api/errors'
import { useAuth } from '@/features/auth/AuthProvider'
import { createMyEvent, deleteMyEvent, GoogleCalendarStatus, updateMyEvent, useCalendarConnection, useCalendarEvents, type CalendarEvent } from '@/features/calendar'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { crm2Api, useCrm2Calendar, useCrm2Contacts, useCrm2Meta, useCrm2Write, type BookingRow, type TeamCalendar } from './api'
import { BookDemo, dayOf, myZone, timeOf } from './BookDemo'
import { Crm2Frame, Field, fieldClass, LINK_CHIP } from './shared'

const HOUR_PX = 48

function mondayOf(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7))
  return x
}
const addDays = (d: Date, n: number) => {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}
/** A datetime-local value for an instant, in the viewer's zone. */
const localInput = (ms: number) => new Date(ms - new Date(ms).getTimezoneOffset() * 60_000).toISOString().slice(0, 16)

type Block = { key: string; kind: 'mine' | 'other' | 'demo' | 'busy'; start: number; end: number; title: string; sub?: string; event?: CalendarEvent; booking?: BookingRow }

/** Side-by-side lanes for blocks that overlap within one day. Pure. */
function lanes(blocks: Block[]): Array<Block & { lane: number; of: number }> {
  const sorted = [...blocks].sort((a, b) => a.start - b.start || b.end - a.end)
  const out: Array<Block & { lane: number; of: number }> = []
  let group: Array<Block & { lane: number; of: number }> = []
  let groupEnd = 0
  const close = () => {
    const n = Math.max(1, ...group.map((g) => g.lane + 1))
    for (const g of group) g.of = n
    group = []
  }
  for (const b of sorted) {
    if (b.start >= groupEnd) close()
    const used = new Set(group.filter((g) => g.end > b.start).map((g) => g.lane))
    let lane = 0
    while (used.has(lane)) lane++
    const placed = { ...b, lane, of: 1 }
    group.push(placed)
    out.push(placed)
    groupEnd = Math.max(groupEnd, b.end)
  }
  close()
  return out
}

/**
 * The TapCRMv2 calendar: your own Google Calendar, every booked demo, and when each person on the
 * demo team is busy, week by week. Click a free spot to add an event to your calendar or book a
 * demo; click a demo to move or cancel it; click your own event to change it. Everything is written
 * to Google, so it shows there too.
 */
export function CalendarPage() {
  const [anchor, setAnchor] = useState(() => mondayOf(new Date()))
  const from = anchor
  const to = addDays(anchor, 7)
  const team = useCrm2Calendar(from.toISOString(), to.toISOString())
  const mine = useCalendarEvents(from, to)
  const conn = useCalendarConnection()
  const meta = useCrm2Meta()
  const { email } = useAuth()
  const me = meta.data?.people.find((p) => p.email?.toLowerCase() === email?.toLowerCase())?.id ?? null
  const [show, setShow] = useState({ mine: true, demos: true, busy: true })
  const [who, setWho] = useState<string>('')
  const [newAt, setNewAt] = useState<number | null>(null)
  const [openEvent, setOpenEvent] = useState<CalendarEvent | null>(null)
  const [openBooking, setOpenBooking] = useState<BookingRow | null>(null)

  const blocks = useMemo(() => buildBlocks(team.data, mine.events, show, who, me), [team.data, mine.events, show, who, me])
  const days = Array.from({ length: 7 }, (_, i) => addDays(anchor, i))
  const firstHour = Math.min(7, ...blocks.filter((b) => b.kind !== 'busy').map((b) => new Date(b.start).getHours()))
  const lastHour = Math.max(21, ...blocks.filter((b) => b.kind !== 'busy').map((b) => new Date(b.end - 1).getHours() + 1))
  const hours = Array.from({ length: lastHour - firstHour }, (_, i) => firstHour + i)
  const [now] = useState(() => Date.now())

  return (
    <Crm2Frame
      title="Calendar"
      description="Your Google Calendar, every booked demo and when the demo team is busy. Changes here go straight to Google."
      actions={<GoogleCalendarStatus />}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={() => setAnchor(addDays(anchor, -7))} aria-label="Previous week">
            <ChevronLeft className="size-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAnchor(mondayOf(new Date()))}>
            This week
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAnchor(addDays(anchor, 7))} aria-label="Next week">
            <ChevronRight className="size-4" />
          </Button>
        </div>
        <span className="text-sm font-medium">
          {dayOf(from.toISOString())} – {dayOf(addDays(anchor, 6).toISOString())}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-3 text-sm">
          {(
            [
              ['mine', 'My calendar', 'bg-info'],
              ['demos', 'Demos', 'bg-primary'],
              ['busy', 'Team busy', 'bg-text-muted'],
            ] as const
          ).map(([k, label, dot]) => (
            <label key={k} className="flex items-center gap-1.5">
              <input type="checkbox" checked={show[k]} onChange={(e) => setShow({ ...show, [k]: e.target.checked })} />
              <span className={cn('size-2 rounded-full', dot)} /> {label}
            </label>
          ))}
          <select className={cn(fieldClass, 'w-44')} value={who} onChange={(e) => setWho(e.target.value)} aria-label="Whose demos and busy times">
            <option value="">Everyone on the team</option>
            {(team.data?.team ?? []).map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {conn.status === 'connected' && !conn.canWrite ? (
        <p className="mb-3 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning">Your Google Calendar is connected read-only. Use the button at the top right and connect again to add and change events here, and to be booked for demos.</p>
      ) : null}
      {(team.data?.team ?? []).some((h) => !h.bookable) ? (
        <p className="mb-3 rounded-lg bg-muted px-3 py-2 text-xs text-text-secondary">
          Not bookable yet: {(team.data?.team ?? []).filter((h) => !h.bookable).map((h) => `${h.name} (${h.problem})`).join('; ')}.
        </p>
      ) : null}
      {team.error ? <ErrorState error={team.error} onRetry={() => void team.refetch()} /> : null}

      <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
        <div className="grid min-w-[52rem]" style={{ gridTemplateColumns: '3.5rem repeat(7, minmax(0, 1fr))' }}>
          <div className="border-b border-border-soft" />
          {days.map((d) => (
            <div key={d.toISOString()} className={cn('border-b border-l border-border-soft px-2 py-2 text-xs font-medium', d.toDateString() === new Date().toDateString() && 'text-brand')}>
              {d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
            </div>
          ))}
          <div className="relative" style={{ height: hours.length * HOUR_PX }}>
            {hours.map((h, i) => (
              <span key={h} className="absolute right-1 -translate-y-1/2 text-tiny text-text-muted" style={{ top: i * HOUR_PX }}>
                {i ? new Date(2000, 0, 1, h).toLocaleTimeString(undefined, { hour: 'numeric' }) : ''}
              </span>
            ))}
          </div>
          {days.map((d) => {
            const dayStart = new Date(d).setHours(firstHour, 0, 0, 0)
            const dayEnd = new Date(d).setHours(lastHour, 0, 0, 0)
            const mineToday = lanes(blocks.filter((b) => b.end > dayStart && b.start < dayEnd))
            return (
              <div
                key={d.toISOString()}
                className="relative border-l border-border-soft"
                style={{ height: hours.length * HOUR_PX, backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent ${HOUR_PX - 1}px, var(--color-border-soft) ${HOUR_PX - 1}px, var(--color-border-soft) ${HOUR_PX}px)` }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect()
                  const mins = Math.floor(((e.clientY - rect.top) / HOUR_PX) * 2) * 30
                  const at = dayStart + mins * 60_000
                  if (at > Date.now() - 30 * 60_000) setNewAt(at)
                }}
              >
                {dayStart <= now && now < dayEnd ? <div className="pointer-events-none absolute inset-x-0 z-20 h-px bg-danger" style={{ top: ((now - dayStart) / 3_600_000) * HOUR_PX }} /> : null}
                {mineToday.map((b) => {
                  const top = ((Math.max(b.start, dayStart) - dayStart) / 3_600_000) * HOUR_PX
                  const height = Math.max(18, ((Math.min(b.end, dayEnd) - Math.max(b.start, dayStart)) / 3_600_000) * HOUR_PX - 2)
                  return (
                    <button
                      key={b.key}
                      type="button"
                      disabled={b.kind === 'busy'}
                      title={`${b.title}${b.sub ? ` · ${b.sub}` : ''} · ${timeOf(new Date(b.start).toISOString())}–${timeOf(new Date(b.end).toISOString())}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (b.booking) setOpenBooking(b.booking)
                        else if (b.event) setOpenEvent(b.event)
                      }}
                      className={cn(
                        'absolute z-10 overflow-hidden rounded-md border px-1.5 py-0.5 text-left text-tiny leading-tight',
                        b.kind === 'demo' && 'border-brand bg-primary text-primary-foreground',
                        b.kind === 'mine' && 'border-info bg-info-soft text-foreground',
                        b.kind === 'other' && 'border-border bg-muted text-foreground',
                        b.kind === 'busy' && 'cursor-default border-dashed border-border bg-muted/70 text-text-muted',
                      )}
                      style={{ top, height, left: `calc(${(b.lane / b.of) * 100}% + 2px)`, width: `calc(${100 / b.of}% - 4px)` }}
                    >
                      <span className="block truncate font-medium">{b.title}</span>
                      {b.sub ? <span className="block truncate opacity-80">{b.sub}</span> : null}
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
      <p className="mt-2 text-xs text-text-muted">Times in your zone ({myZone()}). Demo hours are set in the {team.data?.timezone ?? 'booking'} zone in Settings → Demo booking.</p>

      {newAt !== null ? <NewDialog at={newAt} canWrite={conn.status === 'connected' && conn.canWrite} onClose={() => setNewAt(null)} /> : null}
      {openEvent ? <EventDialog event={openEvent} canWrite={conn.canWrite} onClose={() => setOpenEvent(null)} /> : null}
      {openBooking ? <BookingDialog booking={openBooking} team={team.data?.team ?? []} onClose={() => setOpenBooking(null)} /> : null}
    </Crm2Frame>
  )
}

function buildBlocks(team: TeamCalendar | undefined, events: CalendarEvent[], show: { mine: boolean; demos: boolean; busy: boolean }, who: string, me: string | null): Block[] {
  const out: Block[] = []
  const bookings = (team?.bookings ?? []).filter((b) => !who || b.hostId === who)
  const bookedEvents = new Set((team?.bookings ?? []).map((b) => b.eventId))
  if (show.mine)
    for (const e of events) {
      if (e.allDay || bookedEvents.has(e.id)) continue
      out.push({ key: `e:${e.id}`, kind: e.mine ? 'mine' : 'other', start: Date.parse(e.start), end: Date.parse(e.end), title: e.title, sub: e.organizer && !e.mine ? e.organizer : undefined, event: e })
    }
  if (show.demos)
    for (const b of bookings) out.push({ key: `b:${b.id}`, kind: 'demo', start: Date.parse(b.start), end: Date.parse(b.end), title: `Demo · ${b.account?.name ?? b.contact?.name ?? ''}`, sub: b.hostName ?? undefined, booking: b })
  if (show.busy && team) {
    for (const h of team.team) {
      if (who && h.id !== who) continue
      if (show.mine && h.id === me) continue
      const theirDemos = (team.bookings ?? []).filter((b) => b.hostId === h.id).map((b) => [Date.parse(b.start), Date.parse(b.end)] as const)
      for (const x of team.busy[h.id] ?? []) {
        const s = Date.parse(x.start)
        const e = Date.parse(x.end)
        if (theirDemos.some(([a, b]) => a === s && b === e)) continue
        out.push({ key: `x:${h.id}:${x.start}`, kind: 'busy', start: s, end: e, title: `${h.name.split(' ')[0]} busy` })
      }
    }
  }
  return out
}

/** A free spot was clicked: an event on my calendar, or a demo with someone. */
function NewDialog({ at, canWrite, onClose }: { at: number; canWrite: boolean; onClose: () => void }) {
  const [tab, setTab] = useState<'demo' | 'event'>('demo')
  const [q, setQ] = useState('')
  const [contact, setContact] = useState<{ id: string; name: string; email: string | null } | null>(null)
  const contacts = useCrm2Contacts(q.trim())
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {dayOf(new Date(at).toISOString())}, {timeOf(new Date(at).toISOString())}
          </DialogTitle>
          <DialogDescription>Book a demo with a contact, or add an event to your own Google Calendar.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 border-b border-border-soft" role="tablist">
          {(
            [
              ['demo', 'Book a demo'],
              ['event', 'Event on my calendar'],
            ] as const
          ).map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={cn('-mb-px border-b-2 px-3 py-2 text-sm', tab === k ? 'border-brand font-medium' : 'border-transparent text-muted-foreground')}>
              {label}
            </button>
          ))}
        </div>
        {tab === 'demo' ? (
          contact ? (
            <div className="grid gap-2">
              <p className="text-sm">
                With <span className="font-medium">{contact.name}</span>{' '}
                <button type="button" className={LINK_CHIP} onClick={() => setContact(null)}>
                  Change
                </button>
              </p>
              <BookDemo contactId={contact.id} contactName={contact.name.split(' ')[0] ?? contact.name} hasEmail={Boolean(contact.email)} initialStart={new Date(at).toISOString()} onBooked={onClose} />
            </div>
          ) : (
            <div className="grid gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-text-muted" />
                <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find the contact: name, company, email" className="h-9 pl-8" aria-label="Find a contact" />
              </div>
              <ul className="max-h-64 divide-y divide-border-soft overflow-y-auto rounded-lg border border-border-soft">
                {(contacts.data ?? []).slice(0, 30).map((c) => (
                  <li key={c.id}>
                    <button type="button" className="grid w-full px-3 py-2 text-left hover:bg-muted/60" onClick={() => setContact({ id: c.id, name: c.name, email: c.email })}>
                      <span className="text-sm font-medium">{c.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {c.accountName ?? 'No account'}
                        {c.email ? ` · ${c.email}` : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )
        ) : canWrite ? (
          <EventForm initial={{ summary: '', start: at, end: at + 30 * 60_000, description: '' }} onDone={onClose} />
        ) : (
          <p className="text-sm text-muted-foreground">Connect your Google Calendar with editing allowed (top right) to add events from here.</p>
        )}
      </DialogContent>
    </Dialog>
  )
}

function EventForm({ id, initial, onDone }: { id?: string; initial: { summary: string; start: number; end: number; description: string }; onDone: () => void }) {
  const qc = useQueryClient()
  const [summary, setSummary] = useState(initial.summary)
  const [start, setStart] = useState(localInput(initial.start))
  const [end, setEnd] = useState(localInput(initial.end))
  const [description, setDescription] = useState(initial.description)
  const [guests, setGuests] = useState('')
  const [meet, setMeet] = useState(!id)
  const [busy, setBusy] = useState(false)
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['gcal', 'events'] })
    void qc.invalidateQueries({ queryKey: ['crm2', 'calendar'] })
  }
  const save = async () => {
    setBusy(true)
    try {
      const attendees = guests
        .split(/[,\s]+/)
        .map((g) => g.trim())
        .filter((g) => g.includes('@'))
        .map((email) => ({ email }))
      const body = { summary: summary.trim(), start: new Date(start).toISOString(), end: new Date(end).toISOString(), description: description.trim() || null, timeZone: myZone(), ...(attendees.length ? { attendees } : {}), ...(meet ? { meet: true } : {}) }
      if (id) await updateMyEvent(id, body)
      else await createMyEvent(body)
      toast.success(id ? 'Event updated in Google Calendar' : 'Added to your Google Calendar')
      refresh()
      onDone()
    } catch (e) {
      toast.error(getErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }
  const remove = async () => {
    if (!id) return
    setBusy(true)
    try {
      await deleteMyEvent(id)
      toast.success('Deleted from your Google Calendar')
      refresh()
      onDone()
    } catch (e) {
      toast.error(getErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="grid gap-3">
      <Field label="Title">
        <Input value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={300} />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Starts">
          <input type="datetime-local" className={fieldClass} value={start} onChange={(e) => setStart(e.target.value)} />
        </Field>
        <Field label="Ends">
          <input type="datetime-local" className={fieldClass} value={end} onChange={(e) => setEnd(e.target.value)} />
        </Field>
      </div>
      <Field label={id ? 'Add guests (emails)' : 'Guests (emails, optional)'} hint="Google emails them the invitation.">
        <Input value={guests} onChange={(e) => setGuests(e.target.value)} placeholder="name@company.com, other@company.com" />
      </Field>
      <Field label="Description">
        <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={8000} />
      </Field>
      {!id ? (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={meet} onChange={(e) => setMeet(e.target.checked)} /> Add a Google Meet link
        </label>
      ) : null}
      <DialogFooter className="gap-2">
        {id ? (
          <Button variant="outline" onClick={() => void remove()} disabled={busy}>
            Delete
          </Button>
        ) : null}
        <Button onClick={() => void save()} disabled={busy || !summary.trim() || !start || !end || Date.parse(end) <= Date.parse(start)}>
          {busy ? 'Saving…' : id ? 'Save' : 'Add to my calendar'}
        </Button>
      </DialogFooter>
    </div>
  )
}

function EventDialog({ event, canWrite, onClose }: { event: CalendarEvent; canWrite: boolean; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{event.title}</DialogTitle>
          <DialogDescription>
            {dayOf(event.start)}, {timeOf(event.start)} – {timeOf(event.end)}
            {event.organizer && !event.mine ? ` · organised by ${event.organizer}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {event.joinUrl ? (
            <a className={LINK_CHIP} href={event.joinUrl} target="_blank" rel="noreferrer">
              <Video className="size-3.5" /> Join
            </a>
          ) : null}
          {event.htmlLink ? (
            <a className={LINK_CHIP} href={event.htmlLink} target="_blank" rel="noreferrer">
              <ExternalLink className="size-3.5" /> Open in Google Calendar
            </a>
          ) : null}
        </div>
        {event.mine && canWrite ? (
          <EventForm id={event.id} initial={{ summary: event.title === '(No title)' ? '' : event.title, start: Date.parse(event.start), end: Date.parse(event.end), description: event.description ?? '' }} onDone={onClose} />
        ) : (
          <p className="text-sm text-muted-foreground">{event.mine ? 'Connect Google Calendar again with editing allowed to change this here.' : 'Someone else organised this; change it in Google Calendar.'}</p>
        )}
      </DialogContent>
    </Dialog>
  )
}

function BookingDialog({ booking, team, onClose }: { booking: BookingRow; team: TeamCalendar['team']; onClose: () => void }) {
  const { workspaceSlug = '' } = useParams()
  const [mode, setMode] = useState<'view' | 'move' | 'cancel'>('view')
  const [start, setStart] = useState(localInput(Date.parse(booking.start)))
  const [host, setHost] = useState(booking.hostId ?? '')
  const [anyTime, setAnyTime] = useState(false)
  const [reason, setReason] = useState('')
  const [notify, setNotify] = useState(true)
  const move = useCrm2Write((v: { id: string; start: string; hostId: string | null; anyTime: boolean; notify: boolean }) => crm2Api.moveBooking(v.id, v), () => 'Demo moved; Google has told the guest')
  const cancel = useCrm2Write((v: { id: string; reason: string | null; notify: boolean }) => crm2Api.cancelBooking(v.id, v), () => 'Demo cancelled')
  const [opened] = useState(() => Date.now())
  const past = Date.parse(booking.end) < opened
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Demo · {booking.account?.name ?? booking.contact?.name}</DialogTitle>
          <DialogDescription>
            {dayOf(booking.start)}, {timeOf(booking.start)} – {timeOf(booking.end)} · host {booking.hostName ?? 'unknown'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 text-sm">
          {booking.contact ? (
            <p>
              With{' '}
              <Link className={LINK_CHIP} to={paths.crm2Contact(workspaceSlug, booking.contact.id)}>
                {booking.contact.name}
              </Link>
              {booking.contact.email ? <span className="ml-2 text-xs text-muted-foreground">{booking.contact.email}</span> : null}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {booking.meetLink ? (
              <a className={LINK_CHIP} href={booking.meetLink} target="_blank" rel="noreferrer">
                <Video className="size-3.5" /> Google Meet
              </a>
            ) : null}
            {booking.account ? (
              <Link className={LINK_CHIP} to={paths.crm2Account(workspaceSlug, booking.account.id)}>
                {booking.account.name}
              </Link>
            ) : null}
          </div>
          {booking.notes ? <p className="whitespace-pre-wrap rounded-lg bg-muted px-3 py-2 text-xs">{booking.notes}</p> : null}
        </div>
        {mode === 'move' ? (
          <div className="grid gap-3 border-t border-border-soft pt-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="New time">
                <input type="datetime-local" className={fieldClass} value={start} onChange={(e) => setStart(e.target.value)} />
              </Field>
              <Field label="Host">
                <select className={fieldClass} value={host} onChange={(e) => setHost(e.target.value)} aria-label="Host">
                  {team.filter((h) => h.bookable).map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={anyTime} onChange={(e) => setAnyTime(e.target.checked)} /> Outside the team’s demo hours is fine
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} /> Email the guest the new time
            </label>
          </div>
        ) : mode === 'cancel' ? (
          <div className="grid gap-3 border-t border-border-soft pt-3">
            <Field label="Why (kept on the deal)">
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} /> Email the guest that it is cancelled
            </label>
          </div>
        ) : null}
        <DialogFooter className="gap-2">
          {mode === 'view' ? (
            <>
              <Button variant="outline" onClick={() => setMode('cancel')} disabled={past}>
                Cancel demo
              </Button>
              <Button onClick={() => setMode('move')} disabled={past}>
                Move
              </Button>
            </>
          ) : mode === 'move' ? (
            <>
              <Button variant="ghost" onClick={() => setMode('view')}>
                Back
              </Button>
              <Button disabled={move.isPending || !start} onClick={() => move.mutate({ id: booking.id, start: new Date(start).toISOString(), hostId: host || null, anyTime, notify }, { onSuccess: onClose })}>
                {move.isPending ? 'Moving…' : 'Move the demo'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setMode('view')}>
                Back
              </Button>
              <Button variant="destructive" disabled={cancel.isPending} onClick={() => cancel.mutate({ id: booking.id, reason: reason.trim() || null, notify }, { onSuccess: onClose })}>
                {cancel.isPending ? 'Cancelling…' : 'Cancel the demo'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
